// Story 065 — Register the service worker only on the player surface, and
// inject the manifest <link> only on the player surface. The director
// surface stays plain HTML — no SW, no installability prompt.
//
// The manifest itself is served by the backend at /manifest.webmanifest
// (tenant-aware). We inject a <link rel="manifest"> pointing at the backend
// origin so the cross-origin manifest fetch is correctly attributed.
//
// The SW is generated by vite-plugin-pwa at build time and registered via
// the `virtual:pwa-register` module. We import dynamically so the
// director-surface bundle doesn't pay for it.

const BACKEND_URL =
  import.meta.env.VITE_API_URL || 'http://localhost:3000';

export function injectManifestLink(): void {
  if (typeof document === 'undefined') return;
  if (document.querySelector('link[rel="manifest"]')) return;
  const link = document.createElement('link');
  link.rel = 'manifest';
  // Browser-issued manifest fetches don't allow custom headers, so we
  // can't use X-Swiss-Tenant-Host (which the rest of the frontend uses
  // to signal the tenant on cross-origin XHR). Encode the player
  // hostname in the URL itself; the backend resolver reads ?host=...
  // as a tenant-signal fallback. Without this, the manifest fetch
  // would resolve to the backend's own host (i.e. the public tenant)
  // and an installed bgpl PWA would show up as 'Swiss Magic'.
  const host = encodeURIComponent(window.location.hostname);
  link.href = `${BACKEND_URL}/manifest.webmanifest?host=${host}`;
  // Cross-origin manifest. `anonymous` because the manifest is public (no
  // cookies/auth gate) — using `use-credentials` would require the backend
  // to send Access-Control-Allow-Credentials, which NestJS's enableCors()
  // doesn't do by default. Without a `crossorigin` attribute at all the
  // browser fetches with no-cors and can't parse the response body.
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

export async function registerServiceWorker(): Promise<void> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }
  // Dynamic imports — tree-shake both the plugin and the i18n/toast
  // wiring out of the director bundle when this function isn't called.
  const [{ registerSW }, { showUpdateAvailableToast }, i18nModule] =
    await Promise.all([
      import('virtual:pwa-register'),
      import('@/lib/toast'),
      import('i18next'),
    ]);
  const i18n = i18nModule.default;

  // No `immediate: true` — that would skipWaiting() the moment a new
  // SW is available, silently swapping the JS that a long-lived
  // player tab is running. Instead we let the new SW install in the
  // background and surface a "New version available" toast; the user
  // accepts by clicking Update, which triggers skipWaiting + reload.
  const updateSW = registerSW({
    onNeedRefresh() {
      // i18n.t works outside React for namespaced lookups: the common
      // namespace carries the sw_update_available copy.
      const title = i18n.t('common:sw_update.title');
      const actionLabel = i18n.t('common:sw_update.action');
      showUpdateAvailableToast(title, actionLabel, () => {
        // updateSW(true) tells the waiting SW to skipWaiting + reloads
        // the page so the new bundle takes effect immediately.
        void updateSW(true);
      });
    },
  });
}
